import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useProductScope, productPath, scopeTitle, scopeName } from "../../contexts/ProductScope";
import Spinner from "../../components/common/Spinner";
import RealtimeAreaMap, { AreaPolygon } from "../../components/common/RealtimeAreaMap";
import AreaTimelineChart, { TimelineSeries } from "../../components/realtime/AreaTimelineChart";
import { MapTotals, TotalRow } from "../../components/realtime/MapOverlays";
import { TrendInline, trafficTone } from "../../components/realtime/TrendBits";
import { trafficOf, groupTotal, deductFloor } from "../../realtime/zoneReadings";
import { useNightFloor, NightFloorStore, NightKey, NightRange } from "../../hooks/realtime/useNightFloor";
import CountryBreakdown from "../../components/realtime/CountryBreakdown";
import CountryDailyHeatmap from "../../components/realtime/CountryDailyHeatmap";
import NightFloorControl from "../../components/realtime/NightFloorControl";
import { projectsApi, Project } from "../../services/projectsApi";
import { userMapsApi } from "../../services/userMapsApi";
import { fetchReportDay, fetchCountryDaily, ReportDayData } from "../../services/reportData";
import { RealtimeCountryRow } from "../../services/realtimeApi";
import { useDashboardConfig } from "../../hooks/realtime/useDashboardConfig";
import { dailyCountryRows, countrySplitFromDaily, DOMESTIC } from "../../utils/realtimeCountry";
import {
  aboveFloor,
  buildReportDay,
  countryName,
  dayLabel,
  displayZoneName,
  extraSentences,
  hourRange,
  meanOfSeries,
  osloDayRange,
  osloToday,
  pctChange,
  peakOfSeries,
  reportSentences,
  ReportDay,
  ReportFigures,
  sameHourOn,
  zoneChanges,
  zoneHourly,
  ZoneHours,
} from "../../utils/realtimeReport";
import { theme } from "../../config/theme";
import { useThemeColors } from "../../hooks/useThemeColors";

// Day report: the dashboard's own views frozen on one Oslo day — the map with
// its totals, the timeline, the country breakdown — plus templated summary
// sentences and comparisons with other days. Every figure comes from
// utils/realtimeReport.ts; nothing is model-generated. v1 is manual: generate,
// check, save as PDF. State lives in the URL (?project, ?date, ?compare,
// ?nights, ?title, ?zoom) so a report can be reopened exactly.

const label = "text-[11px] font-mono uppercase tracking-widest text-ink-faint";
const n = (v: number | null | undefined) => (v == null ? "—" : Math.round(v).toLocaleString());
const share = (v: number | null | undefined) => (v == null ? "—" : `${Math.round(v * 100)}%`);
const DAY = 86400000;
const addDays = (date: string, k: number) => new Date(+new Date(`${date}T12:00:00Z`) + k * DAY).toISOString().slice(0, 10);

const Change: React.FC<{ to: number | null | undefined; from: number | null | undefined }> = ({ to, from }) => {
  const c = pctChange(to, from);
  if (c == null) return <span className="text-ink-faint">—</span>;
  const r = Math.round(c * 100);
  return (
    <span className={r > 0 ? "text-ok-ink" : r < 0 ? "text-bad-ink" : "text-ink-muted"}>
      {r > 0 ? "+" : r < 0 ? "−" : "±"}
      {Math.abs(r)}%
    </span>
  );
};

/** One card per view, like the dashboard; never split across printed pages. */
const Card: React.FC<{ title: string; note?: React.ReactNode; children: React.ReactNode }> = ({ title, note, children }) => (
  <section className="mb-6 break-inside-avoid overflow-hidden rounded-2xl border border-paper-300/70 bg-surface shadow-soft print:shadow-none">
    <div className="border-b border-paper-200 px-4 py-3">
      <h2 className="font-display text-base font-semibold text-ink">{title}</h2>
    </div>
    <div className="p-4">{children}</div>
    {note && <p className="px-4 pb-3 text-xs text-ink-faint">{note}</p>}
  </section>
);

/** International per zone per hour, from the country rows. */
function intlByZoneHour(data: ReportDayData, ids: Set<string>) {
  const out = new Map<string, Map<number, number>>();
  for (const r of data.country) {
    if (!ids.has(r.target_id) || r.country === DOMESTIC) continue;
    const t = Math.floor(+new Date(r.timestamp) / 3600000) * 3600000;
    const m = out.get(r.target_id) ?? new Map<number, number>();
    m.set(t, (m.get(t) ?? 0) + r.subscriber_count);
    out.set(r.target_id, m);
  }
  return out;
}

/**
 * The day report: a fixed day prepared and printed, as the last tab of a
 * project dashboard. Only the report itself prints — the page around it must
 * hide its own chrome in print.
 */
export const DayReport: React.FC = () => {
  const productScope = useProductScope();
  const colors = useThemeColors();
  const [params, setParams] = useSearchParams();
  const projectId = params.get("project");
  const date = params.get("date") || osloToday();
  const compare = useMemo(() => (params.get("compare") || "").split(",").filter((d) => d && d !== date), [params, date]);
  const nightsParam = params.get("nights"); // "from,to"
  const title = params.get("title") ?? "";
  const zoomIds = useMemo(() => (params.get("zoom") || "").split(",").filter(Boolean), [params]);
  const config = useDashboardConfig(projectId);

  const [project, setProject] = useState<Project | null>(null);
  const [wktById, setWktById] = useState<Map<string, string>>(new Map());
  const [data, setData] = useState<Record<string, ReportDayData>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countryOff, setCountryOff] = useState(false);
  const [newCompare, setNewCompare] = useState("");
  const [countryDaily, setCountryDaily] = useState<RealtimeCountryRow[] | null>(null);
  const [countryDailyLoading, setCountryDailyLoading] = useState(false);


  useEffect(() => {
    if (!projectId) return;
    projectsApi.get(projectId).then(setProject).catch(() => setError("Could not load the project."));
    userMapsApi
      .loadGeometries()
      .then((g) => setWktById(new Map(g.maps.filter((m) => m.geometry).map((m) => [m.id, m.geometry as string]))))
      .catch(() => {});
  }, [projectId]);

  const days = useMemo(() => [date, ...compare], [date, compare]);

  useEffect(() => {
    const missing = days.filter((d) => !data[d]);
    if (!missing.length) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all(missing.map((d) => fetchReportDay(d).then((r) => [d, r] as const)))
      .then((entries) => {
        if (cancelled) return;
        setData((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        if (entries.some(([, r]) => !r.countryAvailable)) setCountryOff(true);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : "Could not load the data."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days, data]);

  // Daily country rows for the heatmap: two weeks either side of the report day.
  const heatRange = useMemo<[string, string]>(() => {
    const end = addDays(date, 14);
    return [addDays(date, -14), end > osloToday() ? osloToday() : end];
  }, [date]);
  useEffect(() => {
    let cancelled = false;
    setCountryDailyLoading(true);
    fetchCountryDaily(heatRange[0], heatRange[1])
      .then((rows) => !cancelled && setCountryDaily(rows))
      .catch(() => !cancelled && setCountryDaily(null))
      .finally(() => !cancelled && setCountryDailyLoading(false));
    return () => {
      cancelled = true;
    };
  }, [heatRange]);


  // ---------- derived ----------
  const ids = useMemo(() => project?.areaIds ?? [], [project]);
  const idSet = useMemo(() => new Set(ids), [ids]);

  // Night floors: on by default, the nights kept in the address (?nights=from,to)
  // so a report reopens exactly; not remembered on the device.
  const nightFloorStore = useMemo<NightFloorStore>(
    () => ({
      key: projectId ? `report:${projectId}` : null,
      load: () => {
        const [from, to] = (nightsParam || "").split(",");
        return { enabled: true, range: from && to ? { from, to } : null };
      },
      save: ({ range }) => {
        if (!range) return;
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("nights", `${range.from},${range.to}`);
            return next;
          },
          { replace: true }
        );
      },
    }),
    [projectId, nightsParam, setParams]
  );
  // Default baseline nights: up to 14 normal nights before the report day,
  // skipping the 3 nights just before it (an event's visitors arrive early).
  const defaultNights = useCallback(
    (nights: NightKey[]): NightRange | null => {
      const cutoff = addDays(date, -4);
      const eligible = nights.filter((d) => d <= cutoff && d >= addDays(cutoff, -13));
      return eligible.length ? { from: eligible[0], to: eligible[eligible.length - 1] } : null;
    },
    [date]
  );
  const nightFloor = useNightFloor({ store: nightFloorStore, zoneIds: ids, defaultRange: defaultNights });
  const nightRange = useMemo<[string, string] | null>(
    () => (nightFloor.range ? [nightFloor.range.from, nightFloor.range.to] : null),
    [nightFloor.range]
  );
  const reports = useMemo<ReportDay[]>(
    () => days.filter((d) => data[d] && project).map((d) => buildReportDay(d, data[d].presence, data[d].country, ids, config.groups)),
    [days, data, project, ids, config.groups]
  );
  const main = reports.find((r) => r.date === date) ?? null;
  const others = useMemo(() => reports.filter((r) => r.date !== date), [reports, date]);
  const cmp = others[0] ?? null; // the first comparison day drives the per-zone comparisons

  const zones = useMemo(() => {
    const z: Record<string, ZoneHours> = {};
    for (const d of days) if (data[d]) z[d] = zoneHourly(data[d].presence, ids);
    return z;
  }, [days, data, ids]);

  const floorMap = useMemo(() => {
    const m: Record<string, number> = {};
    nightFloor.floorByZone.forEach((f, id) => (m[id] = f.night_floor as number));
    return m;
  }, [nightFloor.floorByZone]);
  const floorSum = Object.values(floorMap).reduce((a, b) => a + b, 0);
  const useFloors = nightFloor.enabled && Object.keys(floorMap).length > 0;

  const above = useMemo(() => {
    if (!useFloors || !main || !zones[main.date]) return null;
    return {
      main: aboveFloor(main, zones[main.date], floorMap),
      cmp: cmp && zones[cmp.date] ? aboveFloor(cmp, zones[cmp.date], floorMap) : undefined,
    };
  }, [useFloors, main, cmp, zones, floorMap]);

  const busiest = main?.peakPresent?.start ?? null;
  const rises = useMemo(
    () => (main && cmp && zones[main.date] && zones[cmp.date] ? zoneChanges({ date: main.date, zones: zones[main.date], busiestHour: busiest }, { date: cmp.date, zones: zones[cmp.date] }) : []),
    [main, cmp, zones, busiest]
  );

  const sentences = useMemo(() => {
    if (!main) return [];
    return [
      ...reportSentences(main, others),
      ...extraSentences({
        main,
        cmpDate: cmp?.date,
        rises,
        above: above ? { main: above.main, cmp: above.cmp, cmpHours: cmp?.hours, floorSum } : undefined,
      }),
    ];
  }, [main, others, cmp, rises, above, floorSum]);

  // ---------- map at the busiest hour ----------
  const mapView = useMemo(() => {
    if (!main || busiest == null || !data[main.date]) return null;
    const zm = zones[main.date];
    const zc = cmp ? zones[cmp.date] : undefined;
    const cmpHour = cmp ? sameHourOn(busiest, main.date, cmp.date) : null;
    const intl = intlByZoneHour(data[main.date], idSet);
    const polygons: AreaPolygon[] = ids
      .filter((id) => wktById.has(id))
      .map((id) => {
        const now = zm?.get(id)?.h.get(busiest) ?? null;
        const before = zc && cmpHour != null ? zc.get(id)?.h.get(cmpHour) ?? null : null;
        const delta = now != null && before != null ? now - before : null;
        return {
          id,
          name: zm?.get(id)?.name,
          wkt: wktById.get(id) as string,
          color: cmp ? trafficTone[trafficOf(delta, before)] : theme.brand500,
          label:
            now != null ? (
              <span className="flex items-center gap-1.5">
                <span className="font-medium text-ink">{n(now)}</span>
                {delta != null && <TrendInline v={Math.round(delta)} />}
              </span>
            ) : undefined,
        };
      });
    const sumAt = (only: Set<string> | null) => {
      const keep = (zoneIds: Iterable<string>) => [...zoneIds].filter((id) => !only || only.has(id));
      const present = groupTotal(keep(zm?.keys() ?? []), (id) => zm?.get(id)?.h.get(busiest));
      const i = groupTotal(keep(intl.keys()), (id) => intl.get(id)?.get(busiest));
      return { present, intl: i ?? undefined };
    };
    const rows: TotalRow[] = [
      { label: `Total ${project?.name ?? ""}`, ...sumAt(null) },
      ...config.groups.filter((g) => g.areaIds.length).map((g) => ({ id: g.id, show: g.show, label: `Total ${g.name}`, ...sumAt(new Set(g.areaIds)) })),
    ];
    // Close-up: only the zones of the saved totals picked under "Close-up map", named.
    const zoomSet = new Set(config.groups.filter((g) => zoomIds.includes(g.id)).flatMap((g) => g.areaIds));
    const closeUp: AreaPolygon[] = polygons
      .filter((p) => p.id && zoomSet.has(p.id))
      .map((p) => ({
        ...p,
        label: p.label ? (
          <span className="flex flex-col leading-tight">
            <span className="text-[10px] text-ink-muted">{displayZoneName(p.name ?? "")}</span>
            {p.label}
          </span>
        ) : undefined,
      }));
    return { polygons, rows, closeUp };
  }, [main, cmp, busiest, data, zones, idSet, ids, wktById, project, config.groups, zoomIds]);

  // ---------- saved totals at the busiest hour, against the same hour on the comparison day ----------
  const totalsTable = useMemo(() => {
    if (!main || busiest == null || !data[main.date]) return [];
    const zm = zones[main.date];
    const zc = cmp ? zones[cmp.date] : undefined;
    const cmpHour = cmp ? sameHourOn(busiest, main.date, cmp.date) : null;
    const im = intlByZoneHour(data[main.date], idSet);
    const ic = cmp && data[cmp.date] ? intlByZoneHour(data[cmp.date], idSet) : undefined;
    const sum = (only: Set<string> | null, get: (id: string) => number | null | undefined) =>
      groupTotal(only ? ids.filter((id) => only.has(id)) : ids, get);
    const aboveAt = (zh: ZoneHours | undefined, hour: number | null) => (id: string) => {
      const v = hour != null ? zh?.get(id)?.h.get(hour) : undefined;
      const f = floorMap[id];
      return v == null || f == null ? null : deductFloor(v, f);
    };
    const row = (label: string, only: Set<string> | null) => ({
      label,
      zones: only ? only.size : ids.length,
      present: sum(only, (id) => zm?.get(id)?.h.get(busiest)),
      presentCmp: zc && cmpHour != null ? sum(only, (id) => zc.get(id)?.h.get(cmpHour)) : null,
      above: useFloors ? sum(only, aboveAt(zm, busiest)) : null,
      aboveCmp: useFloors && zc ? sum(only, aboveAt(zc, cmpHour)) : null,
      intl: sum(only, (id) => im.get(id)?.get(busiest)),
      intlCmp: ic && cmpHour != null ? sum(only, (id) => ic.get(id)?.get(cmpHour)) : null,
    });
    return [
      row(`All zones (${project?.name ?? ""})`, null),
      ...config.groups.filter((g) => g.areaIds.length).map((g) => row(g.name, new Set(g.areaIds))),
    ];
  }, [main, cmp, busiest, data, zones, idSet, ids, floorMap, useFloors, project, config.groups]);

  // ---------- international by zone at the international peak (as in Roaming pulse) ----------
  const intlZones = useMemo(() => {
    if (!main?.peakIntl || !data[main.date]) return [];
    const hour = main.peakIntl.start;
    const im = intlByZoneHour(data[main.date], idSet);
    const ic = cmp && data[cmp.date] ? intlByZoneHour(data[cmp.date], idSet) : undefined;
    const cmpHour = cmp ? sameHourOn(hour, main.date, cmp.date) : null;
    const all = new Map<string, number>();
    for (const r of data[main.date].country) {
      if (!idSet.has(r.target_id) || Math.floor(+new Date(r.timestamp) / 3600000) * 3600000 !== hour) continue;
      all.set(r.target_id, (all.get(r.target_id) ?? 0) + r.subscriber_count);
    }
    return ids
      .map((id) => {
        const now = im.get(id)?.get(hour) ?? null;
        const before = ic && cmpHour != null ? ic.get(id)?.get(cmpHour) ?? null : null;
        const tot = all.get(id);
        return { id, name: zones[main.date]?.get(id)?.name ?? id, now, before, share: now != null && tot ? now / tot : null, change: pctChange(now, before) };
      })
      .filter((z) => z.now != null && z.now > 0)
      .sort((a, b) => (cmp ? (b.change ?? -Infinity) - (a.change ?? -Infinity) : (b.now ?? 0) - (a.now ?? 0)))
      .slice(0, 10);
  }, [main, cmp, data, idSet, ids, zones]);

  // ---------- largest zones by day average ----------
  const largestZones = useMemo(() => {
    if (!main || !zones[main.date]) return [];
    const mean = (h?: Map<number, number>) => {
      const v = h ? Array.from(h.values()) : [];
      return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
    };
    return Array.from(zones[main.date].entries())
      .map(([id, z]) => ({ id, name: z.name, avg: mean(z.h), before: cmp ? mean(zones[cmp.date]?.get(id)?.h) : null, atBusiest: busiest != null ? z.h.get(busiest) ?? null : null }))
      .sort((a, b) => (b.avg ?? 0) - (a.avg ?? 0))
      .slice(0, 10);
  }, [main, cmp, zones, busiest]);

  const heat = useMemo(() => dailyCountryRows(countryDaily, idSet), [countryDaily, idSet]);

  // ---------- charts (comparison days shifted onto the report day's clock) ----------
  const shift = useCallback((d: ReportDay) => (main ? osloDayRange(main.date).from - osloDayRange(d.date).from : 0), [main]);
  const hourSeries = useMemo<TimelineSeries[]>(() => {
    if (!main) return [];
    return [
      { label: `People · ${dayLabel(main.date)}`, color: theme.brand500, data: main.hours.map((h) => ({ x: h.start, y: h.present })), width: 2.5 },
      ...others.map((o) => ({
        label: `People · ${dayLabel(o.date)}`,
        color: theme.flat,
        dashed: true,
        data: o.hours.map((h) => ({ x: h.start + shift(o), y: h.present })),
      })),
      { label: `International · ${dayLabel(main.date)}`, color: colors.css("series-2"), right: true, data: main.hours.map((h) => ({ x: h.start, y: h.intl })) },
    ];
  }, [main, others, colors, shift]);
  const aboveSeries = useMemo<TimelineSeries[]>(() => {
    if (!main || !above) return [];
    return [
      { label: dayLabel(main.date), color: theme.brand500, width: 2.5, data: main.hours.map((h, i) => ({ x: h.start, y: above.main[i] })) },
      ...(cmp && above.cmp
        ? [{ label: dayLabel(cmp.date), color: theme.flat, dashed: true, data: cmp.hours.map((h, i) => ({ x: h.start + shift(cmp), y: above.cmp![i] })) }]
        : []),
    ];
  }, [main, cmp, above, shift]);

  // ---------- country breakdown, via the dashboard's own aggregation ----------
  const countrySplit = useMemo(
    () => (main && data[main.date] ? countrySplitFromDaily(dailyCountryRows(data[main.date].country, idSet).rows) : null),
    [main, data, idSet]
  );
  const cmpCountry = useMemo(() => {
    const m = new Map<string, number>();
    if (cmp && data[cmp.date]) for (const r of dailyCountryRows(data[cmp.date].country, idSet).rows) m.set(r.country, r.avg);
    return m;
  }, [cmp, data, idSet]);

  // ---------- controls ----------
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const addCompare = () => {
    if (!newCompare || newCompare === date || compare.includes(newCompare)) return;
    setParam("compare", [...compare, newCompare].join(","));
    setNewCompare("");
  };
  const download = () => {
    const prev = document.title;
    document.title = `${scopeName(productScope, "Telcofy")} report — ${title || project?.name || "project"} — ${date}`; // becomes the PDF file name
    window.print();
    document.title = prev;
  };

  const figureRows: { name: string; get: (f: ReportFigures) => number | null | undefined; pct?: boolean; when?: (f: ReportFigures) => string }[] = [
    { name: "Busiest hour, people (hourly average)", get: (f) => f.peakPresent?.value, when: (f) => (f.peakPresent ? hourRange(f.peakPresent.start) : "") },
    { name: "Average over the day, people", get: (f) => f.avgPresent },
    { name: "Busiest hour, international", get: (f) => f.peakIntl?.value, when: (f) => (f.peakIntl ? hourRange(f.peakIntl.start) : "") },
    { name: "International share at that hour", get: (f) => f.intlShareAtPeak, pct: true },
  ];
  const aboveFig = above
    ? {
        peak: peakOfSeries(above.main, main!.hours),
        peakCmp: above.cmp && cmp ? peakOfSeries(above.cmp, cmp.hours) : null,
        avg: meanOfSeries(above.main),
        avgCmp: above.cmp ? meanOfSeries(above.cmp) : null,
      }
    : null;

  if (!projectId) return <p className="text-ink-muted">A day report needs a saved project.</p>;

  return (
    <div>
      <div className="mx-auto max-w-5xl print:max-w-none">
        {/* Controls — never printed */}
        <div className="mb-6 rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft print:hidden">
          <div className="flex flex-wrap items-end gap-4">
            <label className="text-xs text-ink-muted">
              Day
              <input
                type="date"
                value={date}
                max={osloToday()}
                onChange={(e) => setParam("date", e.target.value)}
                className="mt-1 block rounded-lg border border-paper-300 bg-surface px-2 py-1 text-sm text-ink"
              />
            </label>
            <div className="text-xs text-ink-muted">
              Compare with
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {compare.map((d) => (
                  <span key={d} className="inline-flex items-center gap-1 rounded-full bg-paper-200 px-2 py-0.5 text-xs text-ink">
                    {dayLabel(d)}
                    <button onClick={() => setParam("compare", compare.filter((x) => x !== d).join(","))} aria-label={`Remove ${d}`} className="text-ink-muted hover:text-ink">
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="date"
                  value={newCompare}
                  max={osloToday()}
                  onChange={(e) => setNewCompare(e.target.value)}
                  className="rounded-lg border border-paper-300 bg-surface px-2 py-1 text-sm text-ink"
                />
                <button onClick={addCompare} disabled={!newCompare} className="rounded-lg border border-paper-300 px-2.5 py-1 text-xs font-medium text-ink transition-colors hover:bg-paper-100 disabled:opacity-40">
                  Add
                </button>
                {!compare.length && (
                  <button onClick={() => setParam("compare", addDays(date, -7))} className="text-xs font-medium text-brand-600 hover:underline">
                    Same weekday last week
                  </button>
                )}
              </div>
            </div>
            <div className="ml-auto flex gap-2">
              <button onClick={download} disabled={!main || loading} className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-600 disabled:opacity-50">
                Download PDF
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <label className="text-xs text-ink-muted">
              Title
              <input
                type="text"
                value={title}
                placeholder={project?.name ?? "Report title"}
                onChange={(e) => setParam("title", e.target.value)}
                className="mt-1 block w-72 rounded-lg border border-paper-300 bg-surface px-2 py-1 text-sm text-ink"
              />
            </label>
            {config.groups.length > 0 && (
              <div className="text-xs text-ink-muted">
                Close-up map of
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {config.groups.map((g) => {
                    const on = zoomIds.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        onClick={() => setParam("zoom", (on ? zoomIds.filter((x) => x !== g.id) : [...zoomIds, g.id]).join(","))}
                        className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${on ? "border-brand-500 bg-brand-50 text-brand-700" : "border-paper-300 text-ink hover:bg-paper-100"}`}
                      >
                        {g.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {!nightFloor.unavailable && nightFloor.nights.length > 0 && (
            <div className="mt-4">
              <NightFloorControl
                loading={nightFloor.loading}
                nights={nightFloor.nights}
                enabled={nightFloor.enabled}
                onToggle={nightFloor.setEnabled}
                fromNight={nightRange?.[0] ?? null}
                toNight={nightRange?.[1] ?? null}
                onRange={nightFloor.setRange}
                status={
                  useFloors
                    ? `Baseline from ${Object.keys(floorMap).length} zones over the selected nights — adds a "night-time baseline removed" chart. Pick normal nights, not the event's.`
                    : "No baseline: the report shows raw counts only."
                }
              />
            </div>
          )}
          <p className="mt-3 text-xs text-ink-faint">
            Download opens your browser's print dialog — choose "Save as PDF". Compare with the same weekday for a fair comparison.
          </p>
        </div>

        {error && <div className="mb-6 rounded-2xl border border-bad-line bg-bad-soft p-4 text-sm text-bad-ink print:hidden">{error}</div>}

        {(loading || !project) && !main ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : main ? (
          <article>
            <header className="mb-6 break-inside-avoid">
              <p className={label}>{scopeTitle(productScope, "Telcofy · Realtime day report", "day report")}</p>
              <h1 className="mt-1 font-display text-3xl font-bold text-ink">{title || project?.name}</h1>
              <p className="mt-1 text-sm text-ink-muted">
                {dayLabel(main.date)} · {main.zonesWithPresence} zones
                {others.length > 0 && <> · compared with {others.map((o) => dayLabel(o.date)).join(", ")}</>}
              </p>
            </header>

            <Card title="Summary">
              <ul className="space-y-1.5 text-sm text-ink">
                {sentences.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-ink-faint">•</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card title="Key figures" note={others.length > 0 ? `Percentages show ${dayLabel(main.date)} relative to each comparison day.` : undefined}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-paper-200 text-left">
                    <th className="py-1.5 pr-3" />
                    <th className="py-1.5 pr-3 text-right font-semibold text-ink">{dayLabel(main.date)}</th>
                    {others.map((o) => (
                      <th key={o.date} className="py-1.5 pr-3 text-right font-medium text-ink-muted">
                        {dayLabel(o.date)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {figureRows.map((row) => (
                    <tr key={row.name} className="border-b border-paper-200 last:border-0">
                      <td className="py-1.5 pr-3 text-ink-muted">{row.name}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-ink">
                        <span className="font-semibold">{row.pct ? share(row.get(main)) : n(row.get(main))}</span>
                        {row.when?.(main) && <span className="ml-1.5 text-xs text-ink-faint">{row.when(main)}</span>}
                      </td>
                      {others.map((o) => (
                        <td key={o.date} className="py-1.5 pr-3 text-right tabular-nums text-ink-muted">
                          {row.pct ? share(row.get(o)) : n(row.get(o))}
                          {!row.pct && (
                            <span className="ml-1.5 text-xs">
                              <Change to={row.get(main)} from={row.get(o)} />
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {aboveFig && (
                    <>
                      <tr className="border-b border-paper-200">
                        <td className="py-1.5 pr-3 text-ink-muted">Busiest hour, above night baseline</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-ink">
                          <span className="font-semibold">{n(aboveFig.peak?.value)}</span>
                          {aboveFig.peak && <span className="ml-1.5 text-xs text-ink-faint">{hourRange(aboveFig.peak.start)}</span>}
                        </td>
                        {others.map((o, i) => (
                          <td key={o.date} className="py-1.5 pr-3 text-right tabular-nums text-ink-muted">
                            {i === 0 ? (
                              <>
                                {n(aboveFig.peakCmp?.value)}
                                <span className="ml-1.5 text-xs">
                                  <Change to={aboveFig.peak?.value} from={aboveFig.peakCmp?.value} />
                                </span>
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="py-1.5 pr-3 text-ink-muted">Average over the day, above night baseline</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums font-semibold text-ink">{n(aboveFig.avg)}</td>
                        {others.map((o, i) => (
                          <td key={o.date} className="py-1.5 pr-3 text-right tabular-nums text-ink-muted">
                            {i === 0 ? (
                              <>
                                {n(aboveFig.avgCmp)}
                                <span className="ml-1.5 text-xs">
                                  <Change to={aboveFig.avg} from={aboveFig.avgCmp} />
                                </span>
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </Card>

            {mapView && mapView.polygons.length > 0 && busiest != null && (
              <Card
                title={`Map at the busiest hour, ${hourRange(busiest)}`}
                note={cmp ? `Labels: people at ${hourRange(busiest)} and the change against the same hour on ${dayLabel(cmp.date)}. Colour: green rising, amber steady, red falling.` : `Labels: people at ${hourRange(busiest)}.`}
              >
                <div className="h-[28rem] overflow-hidden rounded-xl print:h-[20rem]">
                  <RealtimeAreaMap printable polygons={mapView.polygons} fillOpacity={0.28} overlayTopLeft={<MapTotals rows={mapView.rows} />} />
                </div>
              </Card>
            )}

            {mapView && mapView.closeUp.length > 0 && busiest != null && (
              <Card title={`Close-up at ${hourRange(busiest)}: ${config.groups.filter((g) => zoomIds.includes(g.id)).map((g) => g.name).join(", ")}`}>
                <div className="h-[28rem] overflow-hidden rounded-xl print:h-[20rem]">
                  <RealtimeAreaMap printable maxZoom={18} polygons={mapView.closeUp} fillOpacity={0.28} />
                </div>
              </Card>
            )}

            {totalsTable.length > 1 && busiest != null && (
              <Card title="Totals" note={`Saved totals at ${hourRange(busiest)}${cmp ? `, against the same hour on ${dayLabel(cmp.date)}` : ""}.`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-paper-200 text-left text-ink-muted">
                      <th className="py-1.5 pr-2 font-medium">Total</th>
                      <th className="py-1.5 pr-2 text-right font-medium">Zones</th>
                      <th className="py-1.5 pr-2 text-right font-medium">People</th>
                      {useFloors && <th className="py-1.5 pr-2 text-right font-medium">Above baseline</th>}
                      <th className="py-1.5 text-right font-medium">International</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalsTable.map((r, i) => (
                      <tr key={r.label} className={`border-b border-paper-200 last:border-0 ${i === 0 ? "font-medium" : ""}`}>
                        <td className="py-1.5 pr-2 text-ink">{r.label}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums text-ink-muted">{r.zones}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums text-ink">
                          <span className="font-semibold">{n(r.present)}</span>
                          {cmp && (
                            <span className="ml-1.5 text-xs">
                              <Change to={r.present} from={r.presentCmp} />
                            </span>
                          )}
                        </td>
                        {useFloors && (
                          <td className="py-1.5 pr-2 text-right tabular-nums text-ink">
                            <span className="font-semibold">{n(r.above)}</span>
                            {cmp && (
                              <span className="ml-1.5 text-xs">
                                <Change to={r.above} from={r.aboveCmp} />
                              </span>
                            )}
                          </td>
                        )}
                        <td className="py-1.5 text-right tabular-nums text-ink">
                          {n(r.intl)}
                          {cmp && (
                            <span className="ml-1.5 text-xs">
                              <Change to={r.intl} from={r.intlCmp} />
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            <Card title="Hour by hour">
              <div className="h-72 print:h-60">
                <AreaTimelineChart hourAxis animate={false} series={hourSeries} leftAxisLabel="People present" rightAxisLabel="International" empty="No data for this day." />
              </div>
            </Card>

            {aboveSeries.length > 0 && nightRange && (
              <Card
                title="Hour by hour, night-time baseline removed"
                note={`Each zone's baseline is the median of its 02:00–05:00 counts over the nights ${dayLabel(nightRange[0])} – ${dayLabel(nightRange[1])}, subtracted from its hourly figures (never below zero) on every day shown — what remains is the activity above the people who are always there.`}
              >
                <div className="h-64 print:h-56">
                  <AreaTimelineChart hourAxis animate={false} series={aboveSeries} leftAxisLabel="People above baseline" />
                </div>
              </Card>
            )}

            {rises.length > 0 && cmp && (
              <Card
                title={`Zones by change against ${dayLabel(cmp.date)}`}
                note="Zones with a day average of at least 1,000 people, ranked by the change in day average. The last column compares the busiest hour with the same hour on the comparison day."
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-paper-200 text-left text-ink-muted">
                      <th className="py-1.5 pr-2 font-medium" />
                      <th className="py-1.5 pr-2 font-medium">Zone</th>
                      <th className="py-1.5 pr-2 text-right font-medium">Day average</th>
                      <th className="py-1.5 pr-2 text-right font-medium">Change</th>
                      <th className="py-1.5 text-right font-medium">At {busiest != null ? hourRange(busiest) : "busiest hour"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rises.map((z, i) => (
                      <tr key={z.id} className="border-b border-paper-200 last:border-0">
                        <td className="py-1.5 pr-2 text-ink-faint">{i + 1}</td>
                        <td className="py-1.5 pr-2 text-ink">{displayZoneName(z.name)}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums font-semibold text-ink">{n(z.avg)}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          <Change to={z.avg} from={z.avgChange != null ? z.avg / (1 + z.avgChange) : null} />
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          <Change to={z.atHour} from={z.atHourChange != null && z.atHour != null ? z.atHour / (1 + z.atHourChange) : null} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2 print:grid-cols-2">
              <div className="break-inside-avoid">
                <CountryBreakdown
                  loading={false}
                  notEnabled={countryOff}
                  error={null}
                  split={countrySplit}
                  subtitle={`average present over ${dayLabel(main.date)}`}
                  totalLabel="Average present over the day"
                />
              </div>
              <Card title={cmp ? `Largest international groups vs ${dayLabel(cmp.date)}` : "Largest international groups"}>
                {main.topCountries.length ? (
                  <table className="w-full text-sm">
                    <tbody>
                      {main.topCountries.map((c, i) => (
                        <tr key={c.country} className="border-b border-paper-200 last:border-0">
                          <td className="py-1.5 pr-2 text-ink-faint">{i + 1}</td>
                          <td className="py-1.5 pr-2 text-ink">{countryName(c.country)}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums font-semibold text-ink">{n(c.avg)}</td>
                          {cmp && (
                            <td className="py-1.5 text-right tabular-nums">
                              <Change to={c.avg} from={cmpCountry.get(c.country)} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-ink-muted">{countryOff ? "The country breakdown isn't enabled for this account." : "No country data for this day."}</p>
                )}
              </Card>
            </div>

            <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2 print:grid-cols-2">
              {intlZones.length > 0 && main.peakIntl && (
                <Card
                  title="International by zone"
                  note={`At the international peak, ${hourRange(main.peakIntl.start)}${cmp ? `, against the same hour on ${dayLabel(cmp.date)}, ranked by change` : ", ranked by size"}.`}
                >
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-paper-200 text-left text-ink-muted">
                        <th className="py-1.5 pr-2 font-medium" />
                        <th className="py-1.5 pr-2 font-medium">Zone</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Intl</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Share</th>
                        {cmp && <th className="py-1.5 text-right font-medium">Change</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {intlZones.map((z, i) => (
                        <tr key={z.id} className="border-b border-paper-200 last:border-0">
                          <td className="py-1.5 pr-2 text-ink-faint">{i + 1}</td>
                          <td className="py-1.5 pr-2 text-ink">{displayZoneName(z.name)}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums font-semibold text-ink">{n(z.now)}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums text-ink-muted">{share(z.share)}</td>
                          {cmp && (
                            <td className="py-1.5 text-right tabular-nums">
                              <Change to={z.now} from={z.before} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
              <Card title="Largest zones" note="By people present, averaged over the day.">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-paper-200 text-left text-ink-muted">
                      <th className="py-1.5 pr-2 font-medium" />
                      <th className="py-1.5 pr-2 font-medium">Zone</th>
                      <th className="py-1.5 pr-2 text-right font-medium">Day average</th>
                      {cmp && <th className="py-1.5 pr-2 text-right font-medium">Change</th>}
                      <th className="py-1.5 text-right font-medium">At {busiest != null ? hourRange(busiest) : "busiest hour"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {largestZones.map((z, i) => (
                      <tr key={z.id} className="border-b border-paper-200 last:border-0">
                        <td className="py-1.5 pr-2 text-ink-faint">{i + 1}</td>
                        <td className="py-1.5 pr-2 text-ink">{displayZoneName(z.name)}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums font-semibold text-ink">{n(z.avg)}</td>
                        {cmp && (
                          <td className="py-1.5 pr-2 text-right tabular-nums">
                            <Change to={z.avg} from={z.before} />
                          </td>
                        )}
                        <td className="py-1.5 text-right tabular-nums text-ink-muted">{n(z.atBusiest)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>

            {!countryOff && (heat.rows.length > 0 || countryDailyLoading) && (
              <Card
                title="By country, every day"
                note={`Average present per day by source country, ${dayLabel(heatRange[0])} – ${dayLabel(heatRange[1])}; the report day is ${dayLabel(main.date)}. Shading is per row.`}
              >
                <CountryDailyHeatmap
                  loading={countryDailyLoading}
                  notEnabled={false}
                  error={null}
                  days={heat.days}
                  rows={heat.rows}
                  rangeLabel={`${dayLabel(heatRange[0])} – ${dayLabel(heatRange[1])}`}
                />
              </Card>
            )}

            <footer className="mt-2 break-inside-avoid text-xs leading-relaxed text-ink-faint">
              Figures are hourly averages of the estimated number of people present in each zone, from mobile network data, summed
              across zones; a person moving between zones within an hour can be counted in more than one. Times are Oslo time. "International"
              means devices on a non-Norwegian network; its share uses the country breakdown on its own, and "Other countries" is counted in
              international totals but not ranked. Comparisons place the selected days side by side and do not adjust for weather or other events.
              Generated {new Date().toLocaleString("en-GB", { timeZone: "Europe/Oslo" })} Oslo time by Telcofy.
            </footer>
          </article>
        ) : null}
      </div>
    </div>
  );
};

/** The report's old address: the report is a dashboard tab now, so old links and bookmarks land on it. */
const ReportPage: React.FC = () => {
  const productScope = useProductScope();
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set("tab", "report");
  return <Navigate replace to={`${productPath(productScope, "project")}?${next.toString()}`} />;
};

export default ReportPage;
